<template>
  <div>
    <b-row>
      <b-col>
        <h5 v-if="!hideTitle">
          {{ trans('shopping_cart') }}
          <small v-if="hasAdminPanel && !!invoice.recipient && invoice.recipient.name" class="ml-2 text-danger">
            ({{ invoice.recipient.name }})
          </small>
          <small class="ml-2">
            <b-button variant="link" size="sm" class="text-muted p-0" @click="refreshCart">
              <i v-if="!busy" class="fa fa-fw fa-redo"></i>
              <i v-if="busy" class="fa fa-fw fa-circle-notch fa-spin"></i>
            </b-button>
          </small>
        </h5>
      </b-col>
      <b-col v-if="hasAdminPanel" class="col-auto">
        <b-dropdown variant="link" size="sm" toggle-class="text-danger" :text="trans('admin')" right>
          <b-dropdown-item variant="danger" v-if="invoice.status != 'finalized'" v-b-modal.admin-cart-expiration-modal>
            <i class="fa fa-fw fa-edit"></i>
            {{ trans('cart_expiration') }}
          </b-dropdown-item>
          <b-dropdown-item
            variant="danger"
            :href="`/organization/${organization.id}/invoice/ucid/${invoice.ucid}/preview`"
            target="_blank"
          >
            <i class="fa fa-fw fa-file-invoice"></i>
            {{ trans('invoice') }}
          </b-dropdown-item>
          <b-dropdown-item v-if="isDev && invoice.status != 'finalized'" variant="info" @click="debugCmd('reset')">Reset timer</b-dropdown-item>
          <b-dropdown-item v-if="isDev && invoice.status != 'expired'" variant="info" @click="debugCmd('expire')">Expire timer</b-dropdown-item>
          <b-dropdown-item v-if="isDev && invoice.status != 'finalized'" variant="info" @click="debugCmd('finalize')">Finalize</b-dropdown-item>
          <b-dropdown-item v-if="isDev && invoice.status == 'finalized'" variant="info" @click="debugCmd('unfinalize')">Unfinalize</b-dropdown-item>
        </b-dropdown>
      </b-col>
      <b-col v-if="showClose" class="col-auto">
        <h4>
          <button type="button" class="close" @click="$emit('close')">x</button>
        </h4>
      </b-col>
    </b-row>

    <b-overlay :show="busy">
      <b-table
        id="invoice_items"
        ref="table"
        class="bg-white mt-3"
        :items="invoiceItems"
        :fields="fields"
        small
        responsive
        show-empty
      >
        <template #empty>
          <p class="lead text-center text-muted p-3">{{ trans('empty_cart') }}</p>
        </template>
        <template #cell(name)="data">
          <div :class="!!data.item.addon_id ? 'ml-3' :''">
            <div class="d-inline-block">
              <i v-if="data.item.expired" class="fa fa-fw fa-exclamation-circle text-danger" :title="trans('your_reservation_expired')" v-b-tooltip.hover></i>
              <span :class="`${data.item.expired ? 'text-danger' : ''} ${data.item.invoice_item_id ? 'ml-3' : ''}`">{{ data.value }}</span><span v-if="data.item.package">, {{ data.item.booked_location_summary }}</span>
              <small v-if="data.item.package && data.item.package.payment == 'deposit' && data.item.package.deposit > 0" class="d-block text-muted">
                {{ currency(data.item.package.deposit, '1,000[.]00')}} {{ trans('deposit_required').toLowerCase() }}
              </small>
              <b-dropdown
                v-if="adminCanModify(data.item)"
                variant="link"
                size="sm"
                toggle-class="text-danger py-0"
                boundary="window"
              >
                <template #button-content>
                  <small>{{ trans('admin') }}</small>
                </template>
                <b-dropdown-item v-if="adminCanModifyItem" @click="onClickModifyItem(data.item)">
                  <small class="text-danger">
                    <i class="fa fa-fw fa-edit"></i>
                    {{ trans('item') }}
                  </small>
                </b-dropdown-item>
                <b-dropdown-item v-if="data.item.booking && adminCanModifyBooking" @click="onClickModifyBooking(data.item)">
                  <small class="text-danger">
                    <i class="fa fa-fw fa-edit"></i>
                    {{ trans('booking') }}
                  </small>
                </b-dropdown-item>
              </b-dropdown>
              <small v-if="data.item.package && data.item.package.payment == 'offline' && data.item.amount > 0" class="d-block text-muted">
                {{ trans('to_pay_later') }}
              </small>
            </div>
            <small v-if="!!data.item.coupon" class="d-inline-block text-info">
              <i class="fa fa-fw fa-ticket-alt"></i>
              {{ trans('coupon') }} {{ data.item.coupon.code }}
              <dollar :value="-data.item.discount" />
            </small>
            <small v-if="!!data.item.promo_code" class="d-inline-block text-info">
              <i class="fa fa-fw fa-tag"></i>
              {{ trans(data.item.promo_code.is_subsidy ? 'discount' : 'promo_code') }} {{ data.item.promo_code.code }}
              <dollar :value="-data.item.discount" />
            </small>
            <div class="d-inline-block align-top">
              <b-button variant="link" class="py-0" v-if="!!data.item.booking_url" :href="`${iframeUrlPrefix}${data.item.booking_url}${session}`" :title="trans('modify')">
                <i class="fa fa-fw fa-edit"></i>
              </b-button>
              <b-button variant="link" class="py-0" v-if="!!data.item.membership_id" :href="`${iframeUrlPrefix}/org/${organization.id}/memberships#product-card-${data.item.memebrship_id}${session}`" @click="$emit('close')" :title="trans('modify')">
                <i class="fa fa-fw fa-edit"></i>
              </b-button>
              <b-button variant="link" class="py-0" v-if="!!data.item.product_id" :href="`${iframeUrlPrefix}/org/${organization.id}/products#product-card-${data.item.product_id}${session}`" @click="$emit('close')" :title="trans('modify')">
                <i class="fa fa-fw fa-edit"></i>
              </b-button>
              <b-button v-if="!!data.item.package_id || !!data.item.product_id || !!data.item.am_class_id || !!data.item.membership_id" variant="link" size="sm" class="p-0 text-danger" @click="onRemoveItemFromCart(data.item)" :title="trans('remove_from_cart')">
                <i class="fa fa-fw fa-times"></i>
              </b-button>
            </div>
          </div>
        </template>
      </b-table>
      <div v-if="invoice.total > 0" class="text-right mb-3">
        <div v-if="invoice.plus_tax">
          <span class="text-muted mr-2">
              {{ trans('sub_total') }}
          </span>
          <strong>{{ currency(invoice.amount, '1,000.00') }}</strong>
        </div>
        <div v-if="invoice.has_tax && invoice.tax_info.inclusive_tax > 0">
          <span class="text-muted mr-2">
              {{ invoice.tax_info.inclusive_names.join(', ') }} incl.
              <small :title="trans('included_in_prices')" v-b-tooltip.hover>
                <i class="fa fa-fw fa-info-circle"></i>
              </small>
          </span>
          {{ currency(invoice.tax_info.inclusive_tax, '1,000.00') }}
        </div>
        <div v-if="invoice.plus_tax && invoice.tax_info.exclusive_tax > 0">
          <span class="text-muted mr-2">
              {{ invoice.tax_info.exclusive_names.join(', ') }}
          </span>
          {{ currency(invoice.tax_info.exclusive_tax, '1,000.00') }}
        </div>
        <div>
          <span class="text-muted mr-2">
              {{ trans('total') }}
          </span>
          <strong>{{ currency(invoice.total, '1,000.00') }}</strong>
        </div>
        <div v-if="invoice.applied_gift_card_amount > 0">
          <span class="text-muted mr-2">
              {{ trans('gift_card') }}
          </span>
          <strong>{{ currency(-invoice.applied_gift_card_amount, '1,000.00') }}</strong>
        </div>
        <div v-if="invoice.pay_now_amount < invoice.total">
          <span class="text-muted mr-2">
              {{ trans('payable_now') }}
          </span>
          <strong>{{ currency(invoice.pay_now_amount, '1,000.00') }}</strong>
        </div>
        <b-button
          size="sm"
          variant="link"
          class="px-0 shadow-none"
          @click="applyCoupon = !applyCoupon"
          :disabled="busy"
        >
          <i :class="`text-left fa fa-fw fa-${applyCoupon ? 'minus-square' : 'plus-square'}`"></i>
          {{ trans('apply_coupon') }}
        </b-button>
        <b-collapse v-model="applyCoupon" class="mt-2">
          <b-row class="justify-content-end">
            <b-form-group class="col-md-5">
              <b-input-group size="sm">
                <b-form-input
                  v-model="code"
                  :placeholder="trans('code')"
                  :state="validationCode"
                  style="text-transform:uppercase"
                  :disabled="busy"
                  @keydown="onKeydownCode"
                />
                <b-input-group-append>
                  <b-button variant="info" :disabled="busy || !code" @click="onSubmitCode">
                    <span v-if="!busy"><i class="fa fa-fw fa-arrow-right"></i></span>
                    <span v-if="busy"><i class="fa fa-fw fa-circle-notch fa-spin"></i></span>
                  </b-button>
                </b-input-group-append>
              </b-input-group>
              <b-form-invalid-feedback :state="validationCode">
                {{ errorCode }}
              </b-form-invalid-feedback>
            </b-form-group>
          </b-row>
        </b-collapse>
      </div>
      <div v-if="invoice.total == 0" class="text-right mb-3">
        <strong>{{ trans('free') }}</strong>
      </div>
    </b-overlay>

    <b-row v-if="invoice.status != 'finalized'">
      <b-col>
        <b-button v-if="showEmptyCart" variant="outline-danger" class="mt-1" :disabled="busy" @click="confirmEmptyCart = true">
          <i class="fa fa-fw fa-trash"></i>
          <span class="d-none d-md-inline">{{ trans('empty_the_cart') }}</span>
        </b-button>
        <b-button v-if="confirmEmptyCart" variant="danger" class="mt-1 mb-2 mr-2" disabled>
          <i class="fa fa-fw fa-trash"></i>
          <span class="d-none d-md-inline">{{ trans('empty_the_cart') }}</span>
        </b-button>
        <div v-if="confirmEmptyCart" class="d-inline-block">
          <label class="mr-2">
            {{ trans('are_you_sure') }}
          </label>
          <b-button variant="danger" size="sm" class="mr-2" @click="onEmptyCart">
            {{ trans('yes') }}
          </b-button>
          <b-button variant="light" size="sm" @click="confirmEmptyCart = false">
            {{ trans('no') }}
          </b-button>
        </div>
        <b-button v-if="isEmptyingCart" variant="danger" class="mt-1" disabled>
          <i class="fa fa-fw fa-circle-notch fa-spin"></i>
          <span class="d-none d-md-inline">{{ trans('empty_the_cart') }}</span>
        </b-button>
      </b-col>
      <b-col class="col-auto">
        <b-button
          v-if="!hideContinueShopping"
          variant="link"
          size="sm"
          class="mt-1"
          @click="$emit('close')"
        >
          <i class="fa fa-fw fa-reply"></i>
          <span class="d-none d-md-inline">{{ trans('continue_shopping') }}</span>
        </b-button>
        <b-button
          v-if="!hideNext"
          variant="branded"
          class="mt-1"
          :disabled="!canCheckout"
          @click="$emit('click')"
        >
          <span v-if="!!buttonTitle">{{ buttonTitle }}</span>
          <span v-if="!buttonTitle">
            <i class="fa fa-fw fa-shopping-cart"></i>
            {{ trans('checkout') }}
          </span>
        </b-button>
      </b-col>
    </b-row>

    <admin-bookings-modal
      v-if="invoice.status != 'finalized'"
      id="admin-modify-bookings-modal"
      :organization="organization"
      :bookings="modifyBookings"
      :busy="busy"
      @update="refreshCart"
    ></admin-bookings-modal>
    <admin-invoice-items-modal
      v-if="invoice.status != 'finalized'"
      id="admin-modify-items-modal"
      :organization="organization"
      :invoice_items="modifyItems"
      @invoice="$emit('invoice', $event)"
    ></admin-invoice-items-modal>
    <admin-cart-expiration-modal
      v-if="hasAdminPanel && invoice.status != 'finalized'"
      :organization="organization"
      :invoice="invoice"
      @invoice="$emit('invoice', $event)"
    ></admin-cart-expiration-modal>

    <b-row v-if="invoice.status == 'finalized'">
      <b-col>
        <p class="text-success">
          <i class="fa fa-fw fa-check-circle"></i>
          {{ trans('checkout_confirmation_booking') }}
          <b-button variant="branded" size="sm" @click="onEmptyCart">
            {{ trans('create_new_cart') }}
          </b-button>
        </p>
      </b-col>
    </b-row>
  </div>
</template>

<script>
  import Dollar from './../billing/Dollar'
  import AdminBookingsModal from './AdminBookingsModal'
  import AdminInvoiceItemsModal from './AdminInvoiceItemsModal'
  import AdminCartExpirationModal from './AdminCartExpirationModal'

  export default {
    props: {
      organization: Object,
      invoice: Object,
      showClose: {
        type: Boolean,
        default: false,
      },
      buttonTitle: String,
      hideContinueShopping: {
        type: Boolean,
        default: false,
      },
      hideNext: {
        type: Boolean,
        default: false,
      },
      hideTitle: {
        type: Boolean,
        default: false,
      },
    },
    components: {
      Dollar,
      AdminBookingsModal,
      AdminInvoiceItemsModal,
      AdminCartExpirationModal,
    },
    data() {
      return {
        trans: trans,
        moment: moment,
        numeral: numeral,
        currency: _.currency,
        lang: window.lang,
        iframeUrlPrefix: _.iframeUrlPrefix,
        isDev: _.isDev,
        error: null,
        busy: false,
        fields: [
          {key: 'name', label: trans('item')},
          {
            key: 'quantity',
            label: trans('qty'),
            thClass: 'text-right',
            tdClass: 'text-right',
          },
          {
            key: 'price',
            label: trans('price'),
            formatter(value) {
              return _.currency(value, '0,000.00')
            },
            thClass: 'text-right',
            tdClass: 'text-right',
          },
          {
            key: 'amount',
            label: trans('amount'),
            formatter(value) {
              return _.currency(value, '0,000.00')
            },
            thClass: 'text-right',
            tdClass: 'text-right',
          },
        ],
        confirmEmptyCart: false,
        isEmptyingCart: false,
        modify_item: null,
        applyCoupon: false,
        code: null,
        errorCode: null,
      }
    },
    computed: {
      validationCode() {
        return this.errorCode ? false : null
      },
      session() {
        let session = ''
        if (window.session_id) {
          session = `?_session=${window.session_id}`;
        }
        return session
      },
      showEmptyCart() {
        return this.invoice.is_viable_cart &&
        !this.confirmEmptyCart &&
        !this.isEmptyingCart
      },
      hasAdminPanel() {
        return this.organization.auth_user &&
          this.organization.auth_user.microsite_permissions.has_admin_panel
      },
      modifyBookings() {
        if (!this.modify_item || !this.modify_item.booking) return []
        return [this.modify_item.booking]
      },
      modifyItems() {
        if (!this.modify_item) return []
        return _.reduce(this.invoice.invoice_items, (items, item) => {
          if (item.id == this.modify_item.id || item.invoice_item_id == this.modify_item.id) items.push(item)
          return items
        }, [])
      },
      adminCanModifyBooking() {
        return this.organization.auth_user &&
          this.organization.auth_user.microsite_permissions.can_modify_bookings
      },
      adminCanModifyItem() {
        return this.organization.auth_user &&
          this.organization.auth_user.microsite_permissions.can_modify_items
      },
      canCheckout() {
        return this.invoice.status == 'new' &&
          !this.busy &&
          !this.confirmEmptyCart &&
          this.invoice.invoice_items.length > 0
      },
      invoiceItems() {
        // Reorder invoice items to group them by package/product and show add-ons as sub-items
        let map = _.reduce(this.invoice.invoice_items, (map, item) => {
          if (!item.addon_id) {
            if (!map[item.id]) {
              map[item.id] = {
                timestamp: moment(item.start).unix(),
                item: item,
                addon_items: [],
              }
            }
          }
          return map
        }, {})
        _.each(this.invoice.invoice_items, (item) => {
          if (item.addon_id) {
            map[item.invoice_item_id].addon_items.push(item)
          }
        })
        return _.reduce(_.sortBy(map, 'timestamp'), (items, m) => {
          items.push(m.item)
          _.each(m.addon_items, item => {
            items.push(item)
          })
          return items
        }, [])
      },
    },
    methods: {
      onKeydownCode(e) {
        if (e.keyCode == 13) this.onSubmitCode()
      },
      onSubmitCode() {
        this.busy = true
        this.errorCode = null
        let url = `/org/${this.organization.id}/cart/coupon/${encodeURIComponent(this.code)}`
        axios.post(url)
        .then(response => {
          this.busy = false
          this.$emit('invoice', response.data)
        })
        .catch(error => {
          this.busy = false
          this.errorCode = _.errorFromAjax(error)
        })
      },
      adminCanModify(item) {
        return !item.expired &&
          (!!item.package_id || !!item.product_id || !!item.membership_id) &&
          ((item.package_id && this.adminCanModifyBooking) || this.adminCanModifyItem)
      },
      onClickModifyBooking(item) {
        this.modify_item = item
        this.$bvModal.show('admin-modify-bookings-modal')
      },
      onClickModifyItem(item) {
        this.modify_item = item
        this.$bvModal.show('admin-modify-items-modal')
      },
      refreshCart() {
        this.busy = true
        this.error = null
        axios.get(`/org/${this.organization.id}/cart`)
        .then(response => {
          this.busy = false
          this.$emit('invoice', response.data)
        })
        .catch(error => {
          this.busy = false
          this.error = _.errorFromAjax(error)
          _.toastError(this, this.error)
        })
      },
      onRemoveItemFromCart(item) {
        this.busy = true
        this.error = null
        let what = item.product_id ? 'product' : 'package'
        if (item.am_class_id) {
          what = 'class'
        }
        axios.delete(`/org/${this.organization.id}/cart/${what}/${item.id}`)
        .then(response => {
          this.busy = false
          this.$emit('invoice', response.data)
          if (what != 'class') {
            this.removeCreditCardFee()
          }
        })
        .catch(error => {
          this.busy = false
          this.error = _.errorFromAjax(error)
          _.toastError(this, this.error)
        })
      },
      onEmptyCart() {
        this.confirmEmptyCart = false
        this.isEmptyingCart = true
        this.busy = true
        this.error = null
        axios.delete(`/org/${this.organization.id}/cart`)
        .then(response => {
          this.isEmptyingCart = false
          this.busy = false
          this.$emit('invoiceempty', response.data)
        })
        .catch(error => {
          this.isEmptyingCart = false
          this.busy = false
          this.error = _.errorFromAjax(error)
          _.toastError(this, this.error)
        })
      },
      removeCreditCardFee() {
        this.busy = true
        this.error = null
        axios.post(`/org/${this.organization.id}/cart/remove_credit_card_fee`)
        .then(response => {
          this.busy = false
          this.$emit('invoice', response.data)
        })
        .catch(error => {
          this.busy = false
          this.error = _.errorFromAjax(error)
        })
      },
      debugCmd(cmd) {
        this.busy = true
        this.error = null
        axios.post(`/org/${this.organization.id}/cart/debug/${cmd}`)
        .then(response => {
          this.busy = false
          this.$emit('invoice', response.data)
        })
        .catch(error => {
          this.busy = false
          this.error = _.errorFromAjax(error)
          _.toastError(this, this.error)
        })
      },
    },
    mounted() {
      window.cartCard = this
    },
  }
</script>
